import {useCallback, useContext, useEffect, useState, useMemo} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {Calendar} from "primereact/calendar"
import {Dropdown} from "primereact/dropdown"
import useSWR from "swr"

import {OnboardingOrienteeInfo} from "../components/educator/OnboardingOrienteeInfo"
import {PreceptorTable} from "../components/preceptor/PreceptorTable"
import {wrappedPost, wrappedPut} from "../utilities/AxiosHelper"
import RefContext from "../utilities/RefContext"
import {fetcherGet, getJSDate, calcEndDate} from "../utilities/general"

const NURSING_DEGREES = [
    {label: "Associate Degree", value: "associate"},
    {label: "Bachelors Degree", value: "bachelors"},
    {label: "Masters Degree", value: "masters"},
    {label: "Unsure", value: "unsure"},
]

const STEPS = ["New Orientee Info", "Orientation Details", "Choose Preceptors", "Complete"]

const STEP_ORIENTEE_INFO = 0
const STEP_ORIENTATION_DETAILS = 1
const STEP_PRECEPTORS = 2
const STEP_COMPLETE = 3
const SHIFTED_WEEKS_REASON_PURPOSE = "onboarding"

const toIsoDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
    )}-${String(date.getDate()).padStart(2, "0")}`
}

const calculateStartDate = (hireDate, onboardingWeeks) => {
    if (!hireDate) {
        return null
    }

    const calculatedDate = hireDate instanceof Date ? new Date(hireDate) : getJSDate(hireDate)

    if (!(calculatedDate instanceof Date) || Number.isNaN(calculatedDate.getTime())) {
        return null
    }

    const numberOfWeeks = Number(onboardingWeeks ?? 0)
    // Adjusted to Sunday of the week that is `numberOfWeeks` after the hire date.
    calculatedDate.setDate(calculatedDate.getDate() + numberOfWeeks * 7 - calculatedDate.getDay())

    return toIsoDate(calculatedDate)
}

const initial = {
    educator_shifted_weeks: 0,
    employee_id: "",
    experience_level_id: null,
    hasPreceptors: false,
    hire_date: null,
    nursing_program_completed: null,
    orientation_shifted_weeks_reason_id: null,
    standard_orientation_length_weeks: null,
    start_date: null,
    user_type_id: null,
}

const OnboardingPreceptors = ({orientee, changeValues, mutateOrientee}) => {
    const {data: preceptors = []} = useSWR(
        orientee?.unit_id ? `/preceptors?unit-id=${orientee.unit_id}` : null,
        fetcherGet,
        {fallbackData: []}
    )

    const handlePreceptorsChange = (assigned) => {
        changeValues({hasPreceptors: assigned && assigned.length > 0})
        mutateOrientee((currentOrientee) => ({...currentOrientee, preceptors: assigned}), {revalidate: false})
    }

    return (
        <div className="w-100">
            <div className="pb-5">
                <h3 className="fw-bold text-gray-900">Choose Preceptor(s)</h3>

                <div className="text-gray-700 fw-semibold fs-6">
                    Assign preceptors to this orientee, then click "Next."
                </div>
            </div>

            <PreceptorTable
                orientation={orientee}
                preceptors={preceptors}
                onPreceptorsChange={handlePreceptorsChange}
            />
        </div>
    )
}

const isOrientationDetailsComplete = (onboarded) => {
    return (
        onboarded.experience_level_id !== null &&
        onboarded.hire_date !== null &&
        onboarded.start_date !== null &&
        (onboarded.educator_shifted_weeks === 0 || onboarded.orientation_shifted_weeks_reason_id !== null)
    )
}

const determineNextDisabled = (step, onboarded, setNextDisabled) => {
    const disabled =
        (step === STEP_ORIENTEE_INFO &&
            !(String(onboarded.employee_id ?? "").trim() !== "" && Boolean(onboarded.user_type_id))) ||
        (step === STEP_ORIENTATION_DETAILS && !isOrientationDetailsComplete(onboarded)) ||
        (step === STEP_PRECEPTORS && !onboarded.hasPreceptors)
    setNextDisabled(disabled)
}

export default function EducatorOnboardingWizard() {
    const {orientation_id} = useParams()
    const navigate = useNavigate()
    const {dispatch} = useContext(RefContext)

    const {
        data: orientee,
        error,
        isLoading,
        mutate: mutateOrientee,
    } = useSWR(orientation_id ? `/orientees/${orientation_id}` : null, fetcherGet)

    const {data: orientationLengths = []} = useSWR(
        orientee?.unit_id ? `/avg-orientation-length/${orientee.unit_id}` : null,
        fetcherGet,
        {fallbackData: []}
    )

    const {data: defaultOrientationLengths = []} = useSWR(
        orientee?.unit_id ? `/default-orientation-lengths/${orientee.unit_id}` : null,
        fetcherGet,
        {fallbackData: []}
    )

    const {data: shiftedWeeksReasons = []} = useSWR(
        `/educators/orientations/${orientation_id}/shifted-weeks-reasons?purpose=${SHIFTED_WEEKS_REASON_PURPOSE}`,
        fetcherGet,
        {fallbackData: []}
    )

    const [step, setStep] = useState(STEP_ORIENTEE_INFO)
    const [saving, setSaving] = useState(false)
    const [onboarded, setOnboarded] = useState(initial)
    const [nextDisabled, setNextDisabled] = useState(true)

    const changeValues = (values) => {
        let newOnboarded = {...onboarded}
        for (const [key, value] of Object.entries(values)) {
            newOnboarded[key] = value
        }
        setOnboarded(newOnboarded)
        determineNextDisabled(step, newOnboarded, setNextDisabled)
    }

    const shiftedWeekOptions = useMemo(() => {
        const adding = onboarded.educator_shifted_weeks > 0
        return shiftedWeeksReasons
            .filter((reason) => reason.adding === adding || reason.adding === null)
            .map((reason) => ({value: reason.id, label: reason.name}))
    }, [onboarded.educator_shifted_weeks, shiftedWeeksReasons])

    useEffect(() => {
        if (!orientee) {
            return
        }

        const initialHireDate = orientee.hire_date ?? null

        const initialStartDate =
            orientee.start_date && orientee.start_date !== ""
                ? orientee.start_date
                : calculateStartDate(initialHireDate, orientee.unit_num_onboarding_weeks)

        changeValues({
            educator_shifted_weeks: orientee.educator_shifted_weeks ?? 0,
            employee_id: orientee.employee_id ?? "",
            experience_level_id: orientee.experience_level_id ?? null,
            hasPreceptors: orientee.preceptors && orientee.preceptors.length > 0,
            hire_date: initialHireDate,
            nursing_program_completed: orientee.nursing_program_completed ?? null,
            orientation_shifted_weeks_reason_id: orientee.orientation_shifted_weeks_reason_id ?? null,
            standard_orientation_length_weeks: orientee.standard_orientation_length_weeks ?? null,
            start_date: initialStartDate,
            user_type_id: orientee.user_type_id ?? null,
        })
    }, [isLoading])

    const experienceOptions = onboarded.user_type_id
        ? [
              ...orientationLengths.filter((row) => Number(row.type_id) === Number(onboarded.user_type_id)),
              ...defaultOrientationLengths
                  .filter((row) => Number(row.type_id) === Number(onboarded.user_type_id))
                  .filter(
                      (defaultRow) =>
                          !orientationLengths.some(
                              (row) =>
                                  Number(row.type_id) === Number(defaultRow.type_id) &&
                                  row.experience_level_name?.toLowerCase() ===
                                      defaultRow.experience_level_name?.toLowerCase()
                          )
                  ),
          ]
        : []

    const noExperienceLevels = Boolean(onboarded.user_type_id) && experienceOptions.length === 0

    const lengthUnconfigured =
        Boolean(onboarded.experience_level_id) && onboarded.standard_orientation_length_weeks === null

    const next = () => {
        determineNextDisabled(Math.min(step + 1, STEPS.length - 1), onboarded, setNextDisabled)
        setStep((currentStep) => Math.min(currentStep + 1, STEPS.length - 1))
    }

    const back = () => {
        determineNextDisabled(Math.max(step - 1, 0), onboarded, setNextDisabled)
        setStep((currentStep) => Math.max(currentStep - 1, 0))
    }

    const adjustWeeks = (delta) => {
        const minimum = -Number(onboarded.standard_orientation_length_weeks ?? 0)
        const maximum = 52
        const adjustedWeeks = Math.max(minimum, Math.min(maximum, onboarded.educator_shifted_weeks + delta))
        const directionChanged =
            onboarded.educator_shifted_weeks !== 0 &&
            adjustedWeeks !== 0 &&
            Math.sign(onboarded.educator_shifted_weeks) !== Math.sign(adjustedWeeks)
        changeValues({
            educator_shifted_weeks: adjustedWeeks,
            ...(adjustedWeeks === 0 || directionChanged ? {orientation_shifted_weeks_reason_id: null} : {}),
        })
    }

    const updateHireDate = (date) => {
        changeValues({
            hire_date: date ?? null,
            start_date: calculateStartDate(date, orientee?.unit_num_onboarding_weeks),
        })
    }

    const handleUserTypeChange = (event) => {
        /*
         * An experience level for one user type may not be valid for
         * another user type.
         */
        changeValues({
            user_type_id: event.value ?? null,
            educator_shifted_weeks: 0,
            experience_level_id: null,
            orientation_shifted_weeks_reason_id: null,
            standard_orientation_length_weeks: null,
        })
    }

    const handleExperienceChange = (event) => {
        const newExperienceValue = event.value

        const item = experienceOptions.find(
            (row) => String(row.user_type_experience_level_id) === String(newExperienceValue)
        )
        changeValues({
            experience_level_id: newExperienceValue ?? null,
            educator_shifted_weeks: 0,
            orientation_shifted_weeks_reason_id: null,
            standard_orientation_length_weeks: item?.val ?? null,
        })
    }

    const showError = useCallback(
        (err, fallback) => {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: err?.response?.data?.Error ?? fallback,
                timeout: 5000,
            })
        },
        [dispatch]
    )

    const buildPayload = () => {
        return {
            employee_id: onboarded.employee_id,
            user_type_id: onboarded.user_type_id,
            nursing_program_completed: onboarded.nursing_program_completed,
            standard_orientation_length_weeks: onboarded.standard_orientation_length_weeks,
            educator_shifted_weeks: onboarded.educator_shifted_weeks,
            experience_level_id: onboarded.experience_level_id,
            hire_date: toIsoDate(getJSDate(onboarded.hire_date)) ?? null,
            start_date: toIsoDate(getJSDate(onboarded.start_date)) ?? null,
            orientation_shifted_weeks_reason_id:
                onboarded.educator_shifted_weeks !== 0 ? onboarded.orientation_shifted_weeks_reason_id : null,
        }
    }

    const save = async () => {
        setSaving(true)
        try {
            await wrappedPut(`/orientees/${orientation_id}`, buildPayload())
            return true
        } catch (err) {
            showError(err, "An error occurred while saving.")
            return false
        } finally {
            setSaving(false)
        }
    }

    const handleSaveAndClose = async () => {
        const saved = await save()

        if (saved) {
            navigate("/educator-onboarding")
        }
    }

    const handleSubmit = async () => {
        setSaving(true)

        try {
            await wrappedPost(`/orientees/${orientation_id}/submit`, buildPayload())

            dispatch("showToast", {
                show: true,
                variant: "success",
                text: "Onboarding complete.",
                timeout: 5000,
            })

            navigate("/educator-onboarding")
        } catch (err) {
            showError(err, "An error occurred while submitting.")
        } finally {
            setSaving(false)
        }
    }

    if (isLoading) {
        return null
    }

    if (error || !orientee) {
        return (
            <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center">
                <div className="text-gray-700 fw-semibold fs-4">Orientation not found.</div>
            </div>
        )
    }

    const orienteeName = `${orientee.first_name} ${orientee.last_name}`

    return (
        <div className="d-flex flex-column flex-grow-1" style={{minHeight: 0}}>
            <div className="bg-gray-700 flex-shrink-0">
                <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-2 px-6 px-lg-10 py-4">
                    <h3 className="m-0 text-white">
                        Complete Onboarding: {orientee.first_name?.[0]}. {orientee.last_name}
                    </h3>

                    <button
                        type="button"
                        className="btn btn-dark btn-sm align-self-end align-self-md-auto"
                        onClick={handleSaveAndClose}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save/Close"}
                    </button>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto" style={{minHeight: 0}}>
                <div className="stepper stepper-links d-flex flex-column">
                    <div className="stepper-nav justify-content-center d-flex py-2 mt-8">
                        {STEPS.map((title, index) => (
                            <div
                                key={title}
                                className={`stepper-item${index === step ? " current" : ""}${
                                    index < step ? " completed" : ""
                                }`}
                            >
                                <h3 className="stepper-title">{title}</h3>
                            </div>
                        ))}
                    </div>

                    <form
                        className="mx-auto w-100 mw-900px py-5"
                        noValidate
                        onSubmit={(event) => event.preventDefault()}
                    >
                        {step === STEP_ORIENTEE_INFO && (
                            <OnboardingOrienteeInfo
                                orientee={orientee}
                                onboarded={onboarded}
                                changeValues={changeValues}
                                changeUserType={handleUserTypeChange}
                            />
                        )}

                        {step === STEP_ORIENTATION_DETAILS && (
                            <div className="w-100">
                                <div className="pb-5">
                                    <h3 className="fw-bold d-flex align-items-center text-gray-900">
                                        Orientation Details
                                    </h3>

                                    <div className="text-gray-700 fw-semibold fs-6">
                                        Complete the information not provided by Infor.
                                    </div>
                                </div>

                                <div className="row">
                                    <ReadonlyField label="Facility" value={orientee.facility_name} required />

                                    <ReadonlyField label="Unit" value={orientee.unit_name} required />
                                </div>

                                <div className="row">
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label mb-2 required">
                                            Experience Level <span className="text-danger">(Required by Educator)</span>
                                        </label>

                                        <Dropdown
                                            value={onboarded.experience_level_id}
                                            options={experienceOptions}
                                            optionLabel="experience_level_name"
                                            optionValue="user_type_experience_level_id"
                                            onChange={handleExperienceChange}
                                            placeholder={noExperienceLevels ? "None configured" : "Select Level"}
                                            disabled={noExperienceLevels || !onboarded.user_type_id}
                                            className="w-100"
                                        />

                                        {noExperienceLevels && (
                                            <div className="form-text text-danger">
                                                No experience levels are configured for this unit and role.
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label mb-2">Nursing Degree Program Completed</label>

                                        <Dropdown
                                            value={onboarded.nursing_program_completed}
                                            options={NURSING_DEGREES}
                                            onChange={(event) => changeValues({nursing_program_completed: event.value})}
                                            placeholder="Select Degree"
                                            className="w-100"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label required">Unit Hire Date</label>

                                        <Calendar
                                            showIcon
                                            className="w-100 p-dropdown-tall mb-4"
                                            baseZIndex={1500}
                                            placeholder="Unit Hire Date"
                                            value={getJSDate(onboarded.hire_date)}
                                            onChange={(event) => updateHireDate(event.value)}
                                        />
                                    </div>

                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label required">Orientation Start Date</label>

                                        <Calendar
                                            showIcon
                                            className="w-100 p-dropdown-tall mb-4"
                                            baseZIndex={1500}
                                            placeholder="Orientation Start Date"
                                            value={getJSDate(onboarded.start_date)}
                                            onChange={(event) => changeValues({start_date: toIsoDate(event.value)})}
                                            disabledDays={[1, 2, 3, 4, 5, 6]}
                                        />

                                        <div className="form-text">
                                            {`Unit Hire Date + ${orientee?.unit_num_onboarding_weeks} Onboarding weeks`}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label">Standard Orientation Length (Weeks)</label>

                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            value={onboarded.standard_orientation_length_weeks ?? ""}
                                            readOnly
                                        />

                                        {!onboarded.experience_level_id && (
                                            <div className="form-text">
                                                Select an experience level to load the standard length.
                                            </div>
                                        )}

                                        {lengthUnconfigured && (
                                            <div className="form-text text-danger">
                                                No standard orientation length is configured for this experience level.
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label">Calculated Orientation End Date</label>

                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            value={calcEndDate(
                                                onboarded.start_date,
                                                onboarded.standard_orientation_length_weeks
                                            )}
                                            readOnly
                                        />

                                        <div className="form-text">
                                            Orientation Start Date + Standard Orientation Length
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label">Add/Subtract Weeks to Orientation</label>

                                        <div className="input-group">
                                            <button
                                                className="btn btn-icon btn-primary h-auto"
                                                type="button"
                                                onClick={() => adjustWeeks(-1)}
                                                disabled={onboarded.standard_orientation_length_weeks === null}
                                            >
                                                <i className="ki-duotone ki-minus fs-2" />
                                            </button>

                                            <input
                                                type="number"
                                                className="form-control text-center"
                                                readOnly
                                                value={onboarded.educator_shifted_weeks}
                                            />

                                            <button
                                                className="btn btn-icon btn-primary h-auto"
                                                type="button"
                                                onClick={() => adjustWeeks(1)}
                                                disabled={onboarded.standard_orientation_length_weeks === null}
                                            >
                                                <i className="ki-duotone ki-plus fs-2" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label">Expected Orientation End Date</label>

                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            value={calcEndDate(
                                                onboarded.start_date,
                                                onboarded.standard_orientation_length_weeks +
                                                    onboarded.educator_shifted_weeks
                                            )}
                                            readOnly
                                        />

                                        {onboarded.educator_shifted_weeks !== 0 && (
                                            <div className="form-text">
                                                {`${onboarded.educator_shifted_weeks} weeks from calculated orientation end date`}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {onboarded.educator_shifted_weeks !== 0 && (
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-8">
                                            <label className="form-label required">
                                                Reason for{" "}
                                                {onboarded.educator_shifted_weeks < 0 ? "Subtraction" : "Addition"}
                                            </label>

                                            <Dropdown
                                                options={shiftedWeekOptions}
                                                value={onboarded.orientation_shifted_weeks_reason_id}
                                                onChange={(event) =>
                                                    changeValues({orientation_shifted_weeks_reason_id: event.value})
                                                }
                                                placeholder="Select a reason"
                                                className="w-100"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {step === STEP_PRECEPTORS && (
                            <OnboardingPreceptors
                                orientee={orientee}
                                changeValues={changeValues}
                                mutateOrientee={mutateOrientee}
                            />
                        )}

                        {step === STEP_COMPLETE && (
                            <div className="w-100 text-center">
                                <div className="pb-12">
                                    <h1 className="fw-bold text-gray-900">OARS Onboarding Complete</h1>

                                    <div className="fw-semibold text-gray-700 fs-4">
                                        {orienteeName} will be added to your Active Orientees and assigned to your
                                        selected preceptors.
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between px-6 px-lg-10 py-4 border-top bg-body flex-shrink-0">
                <div className="me-2">
                    <button
                        type="button"
                        className="btn btn-lg btn-light btn-flex btn-active-color-primary me-3"
                        onClick={back}
                        disabled={step === STEP_ORIENTEE_INFO}
                    >
                        <i className="fas fa-arrow-circle-left ms-2 fs-4" /> Back
                    </button>
                </div>

                <div>
                    {step === STEP_COMPLETE ? (
                        <button
                            type="button"
                            className="btn btn-lg btn-primary"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? (
                                "Submitting..."
                            ) : (
                                <>
                                    Submit <i className="fas fa-arrow-circle-right ms-2 fs-4" />
                                </>
                            )}
                        </button>
                    ) : (
                        <button type="button" className="btn btn-lg btn-primary" disabled={nextDisabled} onClick={next}>
                            Next <i className="fas fa-arrow-circle-right ms-2 fs-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function ReadonlyField({label, value, required = false}) {
    return (
        <div className="col-12 col-md-6 mb-8">
            <label className={`form-label${required ? " required" : ""}`}>{label}</label>

            <input type="text" className="form-control form-control-solid" value={value ?? ""} readOnly />
        </div>
    )
}
